import { Elements, ElementsConsumer } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { RootState } from '../../../redux/store';
import CustomLoader from '../../CustomLoader/CustomLoader';
import { subscriptionActions } from '../../../redux/subscription/subscriptionSlice';
import { getPaymentByOrganizationId } from '../../../api/paymentApi';
import UpdateBillingDetails from '../UpdateBillingDetails';

function UpdateBillingDetailsWrapper() {
  const clientSecret = useAppSelector((state: RootState) => state.subscription.clientSecret);
  const stripePromise = loadStripe(`${process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY}`);
  const dispatch = useAppDispatch();
  const stripeAccountId = useAppSelector((state: RootState) => state.auth.stripeAccountId);
  const organizationId = useAppSelector((state: RootState) => state.auth.organizationId);
  const [loading, setLoading] = useState<boolean>(true);
  const [paymentAmount, setPaymentAmount] = useState<number | null>(null);

  useEffect(() => {
    if (stripeAccountId && paymentAmount) {
      dispatch(
        subscriptionActions.fetchPaymentIntentSecretAsync({ customerId: stripeAccountId, amount: paymentAmount }),
      );
    }
  }, [dispatch, stripeAccountId]);
  useEffect(() => {
    const fetchPaymentAmountAndClientSecret = async () => {
      try {
        if (!organizationId || !stripeAccountId) {
          throw new Error('Organization ID or Stripe Account ID is missing');
        }

        const paymentsResponse = await getPaymentByOrganizationId(organizationId);

        if (paymentsResponse.status !== 200) {
          throw new Error('Failed to fetch payments data');
        }

        const paymentsData = paymentsResponse.data;

        const overduePayments = paymentsData.filter((payment: any) => payment.paymentStatus === 'overdue');

        if (overduePayments.length === 0) {
          throw new Error('No overdue payments found.');
        }

        overduePayments.sort(
          (a: any, b: any) => new Date(b.paidDateAndTime).getTime() - new Date(a.paidDateAndTime).getTime(),
        );

        const latestOverduePayment = overduePayments[0];
        const { amount } = latestOverduePayment;

        if (amount <= 0) {
          throw new Error('Invalid payment amount.');
        }

        setPaymentAmount(amount);

        dispatch(subscriptionActions.fetchPaymentIntentSecretAsync({ customerId: stripeAccountId, amount }));
      } catch (error) {
        console.error('Error fetching payment amount and client secret:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentAmountAndClientSecret();
  }, [dispatch, organizationId, stripeAccountId]);

  if (loading || !clientSecret) {
    return <CustomLoader />;
  }
  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
      }}
    >
      <ElementsConsumer>
        {({ stripe, elements }) => (
          <UpdateBillingDetails stripe={stripe} elements={elements} paymentAmount={paymentAmount} />
        )}
      </ElementsConsumer>
    </Elements>
  );
}

export default UpdateBillingDetailsWrapper;
